import { useMemo } from 'react';

import {
  definedAccessor,
  xAxisAccessor as x,
  yAxisAccessor as y,
} from './accessors.js';
import { getLineStyle } from './line-style.js';
import { LineCanvas } from '../../../../core/components/canvas/components/LineCanvas.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { TimeseriesDatapoint } from '../../../../core/types/timeseries.js';
import { isIsolatedPoint } from '../../../../core/utils/is-isolated-point.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useSeriesCurve } from '../../../hooks/useSeriesCurve.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useYScales } from '../../../hooks/useYScales.js';
import { useDisplayConfigContext } from '../DisplayConfigContext.js';
import { BaseDisplayProps } from '../shared/types.js';
import { getClampedTimeScale } from '../shared/utils/clamp-value.js';
import { shouldDisplayPoints } from '../shared/utils/display-points.js';

export const LineChartDisplay = ({ xScale }: BaseDisplayProps) => {
  const yScales = useYScales();
  const {
    dataPerVariant: { line: series },
    seriesMap,
    datapointsMap,
  } = useTimeseriesFilteredData();

  const curve = useSeriesCurve();

  const { highlightedSeriesIds } = useDisplayConfigContext();

  const { width, height } = useGridPlotSize();

  const lineSeries = useMemo(() => [...series].reverse(), [series]);

  const isolatedAccessor =
    (displayPoints: boolean) =>
    (datapoints: TimeseriesDatapoint[], index: number) =>
      displayPoints ||
      isIsolatedPoint(datapoints, index, (point) =>
        definedAccessor(point, datapointsMap),
      );

  const clampedScale = getClampedTimeScale(xScale);

  return (
    <div data-testid="line-chart-display">
      {lineSeries.map((series) => {
        const seriesMetadata = safeGet(seriesMap, series);

        const { color, opacity } = getLineStyle(
          seriesMetadata,
          highlightedSeriesIds,
        );

        const displayPoints = !!shouldDisplayPoints(
          seriesMetadata.pointsDisplay,
          width,
          series.datapoints.length,
        );

        return (
          <LineCanvas
            key={seriesMetadata.id}
            data={series.datapoints}
            canvasWidth={width}
            canvasHeight={height}
            x={x(xScale, datapointsMap, clampedScale)}
            y={y(yScales, datapointsMap, seriesMetadata.unit)}
            defined={(point) => definedAccessor(point, datapointsMap)}
            displayScatterPlot={isolatedAccessor(displayPoints)}
            color={() => color}
            opacity={opacity}
            curve={curve}
            enableDotOverflow
          />
        );
      })}
    </div>
  );
};

LineChartDisplay['displayName'] = 'LineChartDisplay';
